import {
  ArrowRightStartOnRectangleIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  DocumentIcon,
  FolderIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  PlusIcon,
  RectangleStackIcon,
  SunIcon,
  UserCircleIcon,
  UsersIcon,
  ViewColumnsIcon,
} from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, NavLink, useMatch, useNavigate } from "react-router";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys, useAction, useEpics, useMe, useProjects, useSprints } from "@/lib/queries";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { NotificationsButton } from "./NotificationsButton";
import { SearchDialog } from "./SearchDialog";
import { Avatar, Kbd } from "./ui";
import { MenuDivider, MenuItem, Popover } from "./ui/Popover";

function NavItem({ to, icon: Icon, children, end }: { to: string; icon: React.ComponentType<{ className?: string }>; children: React.ReactNode; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn("flex h-7 items-center gap-2 rounded-md px-2 text-sm text-muted hover:bg-hover", isActive && "bg-active font-medium text-fg")
      }
    >
      <Icon className="size-4 shrink-0 opacity-80" />
      <span className="truncate">{children}</span>
    </NavLink>
  );
}

function UserMenu() {
  const me = useMe().data!;
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const qc = useQueryClient();
  return (
    <Popover
      className="w-60 p-1"
      trigger={(t) => (
        <button ref={t.ref} onClick={t.onClick} className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 hover:bg-hover">
          <Avatar id={me.id} name={me.name} size={22} />
          <span className="truncate text-sm font-medium" dir="auto">
            {me.name}
          </span>
        </button>
      )}
    >
      {(close) => (
        <>
          <div className="px-2 py-1.5 text-xs text-muted">{me.email}</div>
          <MenuItem icon={UserCircleIcon} onClick={() => (close(), navigate("/account"))}>
            Account settings
          </MenuItem>
          {me.isAdmin && (
            <MenuItem icon={UsersIcon} onClick={() => (close(), navigate("/admin/users"))}>
              Manage users
            </MenuItem>
          )}
          <MenuItem icon={theme === "dark" ? SunIcon : MoonIcon} onClick={toggle}>
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </MenuItem>
          <MenuDivider />
          <MenuItem
            icon={ArrowRightStartOnRectangleIcon}
            onClick={async () => {
              await api.logout();
              qc.clear();
              navigate("/login");
            }}
          >
            Log out
          </MenuItem>
        </>
      )}
    </Popover>
  );
}

function SidebarFrame({ children, footer }: { children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-1 p-2">
        <UserMenu />
        <NotificationsButton />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4 scroll-thin">{children}</div>
      {footer}
    </div>
  );
}

export function Sidebar() {
  const projects = useProjects();
  return (
    <SidebarFrame>
      <NavItem to="/" icon={HomeIcon} end>
        All projects
      </NavItem>
      <div className="mt-4 mb-1 px-2 text-xs font-medium text-faint">Projects</div>
      {projects.data?.map((p) => (
        <NavItem key={p.id} to={`/p/${p.id}`} icon={FolderIcon}>
          {p.name}
        </NavItem>
      ))}
      {projects.data?.length === 0 && <div className="px-2 text-sm text-faint">No projects yet</div>}
    </SidebarFrame>
  );
}

function EpicTreeItem({ epic }: { epic: { id: string; title: string } }) {
  const { pid } = useProject();
  const match = useMatch(`/p/${pid}/epics/${epic.id}`);
  const [open, setOpen] = useState(!!match);
  const sprints = useSprints(pid, epic.id);
  return (
    <div>
      <div className="group flex h-7 items-center rounded-md hover:bg-hover">
        <button type="button" onClick={() => setOpen(!open)} className="rounded p-0.5 text-faint hover:bg-active" aria-label="Toggle sprints">
          <ChevronRightIcon className={cn("size-4 transition-transform rtl:rotate-180", open && "rotate-90 rtl:rotate-90")} />
        </button>
        <NavLink
          to={`/p/${pid}/epics/${epic.id}`}
          className={({ isActive }) => cn("min-w-0 flex-1 truncate ps-1 pe-2 text-sm text-muted", isActive && "font-medium text-fg")}
          dir="auto"
        >
          {epic.title}
        </NavLink>
      </div>
      {open && (
        <div className="ms-4 border-s border-line ps-1">
          {sprints.data?.map((s) => (
            <NavLink
              key={s.id}
              to={`/p/${pid}/sprints/${s.id}`}
              className={({ isActive }) =>
                cn("flex h-7 items-center gap-2 truncate rounded-md px-2 text-[13px] text-muted hover:bg-hover", isActive && "bg-active font-medium text-fg")
              }
            >
              <RectangleStackIcon className="size-3.5 shrink-0 opacity-70" />
              <span className="truncate" dir="auto">
                {s.title}
              </span>
            </NavLink>
          ))}
          {sprints.data?.length === 0 && <div className="px-2 py-1 text-xs text-faint">No sprints</div>}
        </div>
      )}
    </div>
  );
}

export function ProjectSidebar() {
  const { pid, bundle, canEdit } = useProject();
  const epics = useEpics(pid);
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const createEpic = useAction((title: string) => api.createEpic(pid, title), {
    invalidate: [keys.epics(pid)],
    onSuccess: (epic) => navigate(`/p/${pid}/epics/${epic.id}`),
  });

  return (
    <SidebarFrame>
      <Link to="/" className="mb-1 flex h-7 items-center gap-1 rounded-md px-2 text-xs text-faint hover:bg-hover">
        <ChevronRightIcon className="size-3.5 rotate-180 rtl:rotate-0" /> All projects
      </Link>
      <div className="mb-2 flex items-center gap-2 px-2">
        <span className="flex size-6 shrink-0 items-center justify-center rounded bg-accent text-[10px] font-bold text-white">
          {bundle.project.key.slice(0, 2)}
        </span>
        <span className="truncate font-semibold" dir="auto">
          {bundle.project.name}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        className="flex h-7 w-full items-center gap-2 rounded-md px-2 text-sm text-muted hover:bg-hover"
      >
        <MagnifyingGlassIcon className="size-4 opacity-80" />
        <span className="flex-1 text-start">Search</span>
        <Kbd>Ctrl K</Kbd>
      </button>
      <NavItem to={`/p/${pid}`} icon={HomeIcon} end>
        Overview
      </NavItem>
      <NavItem to={`/p/${pid}/tasks`} icon={ViewColumnsIcon}>
        All tasks
      </NavItem>
      <NavItem to={`/p/${pid}/files`} icon={DocumentIcon}>
        Files
      </NavItem>
      <NavItem to={`/p/${pid}/settings`} icon={Cog6ToothIcon}>
        Settings
      </NavItem>

      <div className="group mt-5 mb-1 flex items-center justify-between px-2">
        <span className="text-xs font-medium text-faint">Epics</span>
        {canEdit && (
          <button
            type="button"
            title="New epic"
            disabled={createEpic.isPending}
            onClick={() => createEpic.mutate("Untitled epic")}
            className="rounded p-0.5 text-faint opacity-0 group-hover:opacity-100 hover:bg-hover hover:text-fg focus:opacity-100"
          >
            <PlusIcon className="size-4" />
          </button>
        )}
      </div>
      {epics.data?.map((e) => <EpicTreeItem key={e.id} epic={e} />)}
      {epics.data?.length === 0 && <div className="px-2 text-sm text-faint">No epics yet</div>}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </SidebarFrame>
  );
}
